import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import documentLodgementService from '../../../services/documentLodgementService';
import DocumentCard from './DocumentCard';
import { supabase } from '../../../lib/supabase';
import PayrollIntegrationWizard from './PayrollIntegrationWizard';

const DocumentLodgementPanel = () => {
  const [activeTab, setActiveTab] = useState('payroll_integration');
  const [ableDocuments, setAbleDocuments] = useState([]);
  const [ircDocuments, setIrcDocuments] = useState([]);
  const [lodgementHistory, setLodgementHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('all');
  const [showPayrollWizard, setShowPayrollWizard] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ableData, ircData, historyData] = await Promise.all([
        documentLodgementService?.getAllAbleDocuments(),
        documentLodgementService?.getAllIrcDocuments(),
        documentLodgementService?.getLodgementHistory()
      ]);

      setAbleDocuments(ableData);
      setIrcDocuments(ircData);
      setLodgementHistory(historyData);
    } catch (err) {
      setError(err?.message || 'Failed to load document lodgement data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocumentStatus = async (documentId, newStatus, isIrc = false) => {
    try {
      if (isIrc) {
        await documentLodgementService?.updateIrcDocumentStatus(documentId, newStatus);
      } else {
        await documentLodgementService?.updateAbleDocumentStatus(documentId, newStatus);
      }
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to update document status');
    }
  };

  const handleGenerateSwtDocument = async () => {
    try {
      setLoading(true);
      // Get latest payroll run
      const { data: payrollRuns } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(1);
      
      if (payrollRuns && payrollRuns?.length > 0) {
        const currentMonth = new Date()?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        await documentLodgementService?.generateSwtDocument(payrollRuns?.[0]?.id, currentMonth);
        await loadData();
      } else {
        setError('No payroll runs found');
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate SWT document');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSoeDocument = async () => {
    try {
      setLoading(true);
      // Get latest payroll run
      const { data: payrollRuns } = await supabase
        ?.from('payroll_runs')
        ?.select('*')
        ?.order('created_at', { ascending: false })
        ?.limit(1);
      
      if (payrollRuns && payrollRuns?.length > 0) {
        const currentMonth = new Date()?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        await documentLodgementService?.generateSoeDocument(payrollRuns?.[0]?.id, currentMonth);
        await loadData();
      } else {
        setError('No payroll runs found');
      }
    } catch (err) {
      setError(err?.message || 'Failed to generate SOE document');
    } finally {
      setLoading(false);
    }
  };

  const documentTypeOptions = [
    { value: 'all', label: 'All Documents' },
    { value: 'swt_monthly', label: 'SWT Monthly' },
    { value: 'soe_monthly', label: 'SOE Monthly' },
    { value: 'able_payroll', label: 'ABLE Payroll' }
  ];

  const tabs = [
    { id: 'payroll_integration', label: 'Payroll Integration', icon: 'Building' },
    { id: 'irc', label: 'IRC Tax (SWT/SOE)', icon: 'Receipt' },
    { id: 'history', label: 'Lodgement History', icon: 'History' }
  ];

  const filteredIrcDocuments = ircDocuments?.filter(doc => {
    if (documentTypeFilter === 'all') return true;
    return doc?.documentType === documentTypeFilter;
  });

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Icon name="FileCheck" size={20} color="var(--color-primary)" />
            <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
              Payroll Integration & Document Lodgement
            </h3>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            iconName={loading ? 'Loader2' : 'RefreshCw'}
            iconPosition="left"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
        <p className="caption text-xs md:text-sm text-muted-foreground">
          Integrate with PNG payroll providers (ABLE, Smoothpay, BSP Quick Pay, ALESCO) and manage IRC Tax documents
        </p>
      </div>

      {error && (
        <div className="p-4 bg-error/10 border-b border-error">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <p className="text-error font-medium text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="p-4 border-b border-border">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-caption text-sm transition-smooth whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-white' :'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4 md:p-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
          </div>
        )}

        {!loading && activeTab === 'payroll_integration' && (
          <div>
            {showPayrollWizard ? (
              <PayrollIntegrationWizard
                onComplete={(result) => {
                  setShowPayrollWizard(false);
                  loadData();
                }}
                onCancel={() => setShowPayrollWizard(false)}
              />
            ) : (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-foreground mb-1">
                      PNG Payroll Provider Integration
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Connect with ABLE, Smoothpay, BSP Quick Pay, or ALESCO payroll systems
                    </p>
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setShowPayrollWizard(true)}
                  >
                    Import Payroll Data
                  </Button>
                </div>

                <div className="bg-accent/10 border border-accent rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-2">Supported Integration Methods</p>
                      <ul className="text-xs text-muted-foreground space-y-1">
                        <li>• <strong>API Connection:</strong> Real-time synchronization with payroll provider</li>
                        <li>• <strong>CSV Import:</strong> Upload CSV files exported from payroll system</li>
                        <li>• <strong>Excel Import:</strong> Upload Excel spreadsheets with payroll data</li>
                      </ul>
                      <p className="text-xs text-muted-foreground mt-3">
                        All imported data is automatically validated against PNG labor laws including minimum wage, overtime rates, and NASFUND contributions.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h5 className="text-sm font-semibold text-foreground">Recent Integration Sessions</h5>
                  {/* Display recent integration sessions here */}
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Building" size={48} className="mx-auto mb-3 opacity-50" />
                    <p className="text-sm">No payroll integration sessions yet</p>
                    <p className="text-xs mt-1">Click "Import Payroll Data" to start your first integration</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === 'irc' && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <Select
                options={documentTypeOptions}
                value={documentTypeFilter}
                onChange={setDocumentTypeFilter}
                className="w-48"
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleGenerateSwtDocument}
                >
                  Generate SWT
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleGenerateSoeDocument}
                >
                  Generate SOE
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              {filteredIrcDocuments?.length === 0 ? (
                <div className="py-12 text-center">
                  <Icon name="Receipt" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
                  <p className="caption text-sm text-muted-foreground">No IRC Tax documents found</p>
                </div>
              ) : (
                filteredIrcDocuments?.map(doc => (
                  <DocumentCard
                    key={doc?.id}
                    document={doc}
                    onUpdateStatus={(status) => handleUpdateDocumentStatus(doc?.id, status, true)}
                    isIrc
                  />
                ))
              )}
            </div>
          </div>
        )}

        {!loading && activeTab === 'history' && (
          <div>
            <h4 className="font-heading font-semibold text-sm text-foreground mb-4">
              Lodgement History
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <span className="font-heading font-semibold text-sm text-foreground">Type</span>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <span className="font-heading font-semibold text-sm text-foreground">Period</span>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <span className="font-heading font-semibold text-sm text-foreground">Portal</span>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <span className="font-heading font-semibold text-sm text-foreground">Status</span>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <span className="font-heading font-semibold text-sm text-foreground">Reference</span>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <span className="font-heading font-semibold text-sm text-foreground">Submitted</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lodgementHistory?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-4 py-8 text-center">
                        <p className="caption text-sm text-muted-foreground">No lodgement history found</p>
                      </td>
                    </tr>
                  ) : (
                    lodgementHistory?.map(history => (
                      <tr key={history?.id} className="hover:bg-muted/50 transition-smooth">
                        <td className="px-4 py-3">
                          <span className="font-body font-medium text-sm text-foreground capitalize">
                            {history?.lodgementType?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground">{history?.lodgementPeriod}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-muted-foreground capitalize">
                            {history?.portalType?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-foreground capitalize">
                            {history?.lodgementStatus?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {history?.portalReference ? (
                            <span className="data-text text-xs text-foreground font-mono">
                              {history?.portalReference}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-muted-foreground">
                            {history?.submittedAt ? new Date(history?.submittedAt)?.toLocaleDateString('en-PG') : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentLodgementPanel;